import { Injectable } from '@angular/core';
import { NfcCardModel } from '../../../api/autogenerated';
import { PaymentMethodDetails } from '../../../types/PaymentDetails';
import { Profile } from '../../../types/Profile';
import { NFCCardRequestObject } from '../../../types/requests/NFCCardRequestObject';
import { PayoutFeesResponse } from '../../../types/responses/PayoutFeesResponse';
import { UserProfile } from '../../../types/UserProfile';
import { CoreService } from '../../core.service';
import { SharedProfileData } from '../../data/shared-profile-data';
import { IProfileManagementService } from '../IProfileManagementService';
import {
  mockPayoutFeesList,
  mockProfile,
  mockSecondProfile,
  mockUserProfile,
} from './mock-data';

@Injectable({
  providedIn: 'root',
})
export class MockProfileManagementService implements IProfileManagementService {
  constructor(
    private _coreService: CoreService,
    private sharedProfileData: SharedProfileData,
  ) {}

  private profile: Profile = { ...mockProfile };
  private userProfile: UserProfile = { ...mockUserProfile };
  private secondMockProfile: Profile = { ...mockSecondProfile };

  private payoutFeesList: PayoutFeesResponse[] = mockPayoutFeesList;

  async getProfile(forceUpdate?: boolean): Promise<Profile> {
    console.log('Fetching profile', forceUpdate);
    if (!forceUpdate && this.sharedProfileData.getUserProfile() != null) {
      return this.sharedProfileData.getUserProfile();
    }
    this.sharedProfileData.setUserProfile(this.profile);
    return this._coreService.delayResolve(this.profile);
  }

  async updateProfile(profile: Partial<Profile>): Promise<Profile> {
    console.log('Updating profile', profile);
    this.profile = { ...this.profile, ...profile };
    return this._coreService.delayResolve(this.profile);
  }

  async updateUserProfile(
    userProfile: Partial<UserProfile>,
  ): Promise<UserProfile> {
    console.log('Updating user profile', userProfile);
    this.userProfile = { ...this.userProfile, ...userProfile };
    return this._coreService.delayResolve(this.userProfile);
  }

  async addPaymentMethod(
    paymentMethod: PaymentMethodDetails,
  ): Promise<Profile> {
    console.log('Adding payment method', paymentMethod);
    this.profile.paymentMethodDetails.push(paymentMethod);
    return this._coreService.delayResolve(this.profile);
  }

  async removePaymentMethod(accountNumber: string): Promise<Profile> {
    console.log('Removing payment method with account number', accountNumber);
    this.profile.paymentMethodDetails =
      this.profile.paymentMethodDetails.filter(
        (method) => method.accountNumber !== accountNumber,
      );
    return this._coreService.delayResolve(this.profile);
  }

  async setDefaultBankAccount(bankAccountId: string): Promise<Profile> {
    this.profile.paymentMethodDetails.forEach((method) => {
      method.isDefault = method.id === bankAccountId;
    });
    this.sharedProfileData.setUserProfile(this.profile);
    return this._coreService.delayResolve({ ...this.profile });
  }

  async getBankAccounts(_customerId: string): Promise<PaymentMethodDetails[]> {
    return this._coreService.delayResolve([
      ...this.profile.paymentMethodDetails,
    ]);
  }

  async getPayoutFees(
    businessId: string,
    sellVolume: number,
    sellCurrency: string,
  ): Promise<PayoutFeesResponse> {
    console.log(
      'Fetching payout fees for business ID',
      businessId,
      sellCurrency,
    );
    return this._coreService.delayResolve(this.payoutFeesList[2]);
  }

  async postPayoutsPreview(
    businessId: string,
    invoiceId: string,
    reconciliationAmount: number,
  ): Promise<string | null> {
    console.log(
      'Payout preview mock',
      businessId,
      invoiceId,
      reconciliationAmount,
    );
    const d = new Date();
    d.setDate(d.getDate() + 2);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return this._coreService.delayResolve(`${y}-${m}-${day}`);
  }

  async getConversionRate(
    sellCurrency: string,
    buyCurrency: string,
  ): Promise<{
    userConversionRate: number;
    midMarketRate: number;
  }> {
    console.log('Fetching conversion rate', sellCurrency, buyCurrency);
    const mockRate = this.payoutFeesList[0]?.conversionAgainstOneUnit ?? 83.75;
    return this._coreService.delayResolve({
      userConversionRate: mockRate,
      midMarketRate: mockRate,
    });
  }

  async getAllActiveProfiles(): Promise<Profile[]> {
    return this._coreService.delayResolve([
      this.profile,
      this.secondMockProfile,
    ]);
  }

  async getAllProfiles(): Promise<Profile[]> {
    console.log('Fetching all profiles');
    return this._coreService.delayResolve([
      this.profile,
      this.secondMockProfile,
    ]);
  }

  async setProfile(profile: Profile): Promise<Profile> {
    console.log('Setting profile', profile);
    this.profile = profile;
    return this._coreService.delayResolve(this.profile);
  }

  getLogoImage(fileId: string): Promise<Blob> {
    return Promise.resolve(new Blob());
  }

  async getNfcCardDetails(
    userId: string,
  ): Promise<{ success: boolean; data: NfcCardModel; error: string }> {
    return {
      success: true,
      data: {
        id: 'mock-nfc-id',
        user_id: userId,
        name: 'John Doe',
        email: 'john.doe@example.com',
        company: 'Mock Company',
        title: 'CEO',
        mobile: '+1234567890',
        profile_url: 'nfc-card/qwqw-qwqw-qwqw',
        create_time: new Date().toISOString(),
        update_time: new Date().toISOString(),
      },
      error: '',
    };
  }

  async createOrUpdateNfcCard(
    userId: string,
    body: NFCCardRequestObject,
  ): Promise<{ success: boolean; data: NfcCardModel; error: string }> {
    return {
      success: true,
      data: {
        id: 'mock-nfc-id',
        user_id: userId,
        name: body.name,
        email: body.email,
        company: body.company,
        title: body.title,
        mobile: body.mobile,
        external_profile_id: body.external_profile_id,
        profile_url: 'nfc-card/qwqw-qwqw-qwqw',
        create_time: new Date().toISOString(),
        update_time: new Date().toISOString(),
      },
      error: '',
    };
  }

  async getUserId(): Promise<string> {
    return 'sample-user-id';
  }

  async removeCustomerProfilePicture(profile: Profile): Promise<Profile> {
    return this._coreService.delayResolve(profile);
  }
  async sendOTPToUserPhoneNumber(phoneNumber: string): Promise<boolean> {
    console.log('Sending OTP to phone number', phoneNumber);
    return this._coreService.delayResolve(true);
  }
  async verifyOTP(phoneNumber: string, otp: string): Promise<boolean> {
    console.log('Verifying OTP', otp);
    return this._coreService.delayResolve(true);
  }

  async getUserProfile(): Promise<UserProfile> {
    return this._coreService.delayResolve({
      userId: 'sample-user-id',
      status: 'active',
      name: 'John Doe',
      email: 'john.doe@example.com',
      phoneNumber: '+918008123433',
      profilePicture: 'https://example.com/profile-picture.jpg',
      profileImage: new Blob(),
      affiliateManager: true,
    });
  }

  async enableAutoWithdrawal(
    customerProfile: Partial<Profile>,
  ): Promise<boolean> {
    console.log('Enabling auto withdrawal for profile', customerProfile);
    return this._coreService.delayResolve(true);
  }

  async disableAutoWithdrawal(
    customerProfile: Partial<Profile>,
  ): Promise<boolean> {
    console.log('Disabling auto withdrawal for profile', customerProfile);
    return this._coreService.delayResolve(true);
  }

  async setAutoWithdrawalConsent(
    customerProfile: Partial<Profile>,
  ): Promise<boolean> {
    throw new Error('Method not implemented.');
  }

  async isContextCustomerActive(_customerId: string): Promise<boolean> {
    return this._coreService.delayResolve(false);
  }
}
